import { useCallback, useEffect, useRef, useState } from 'react';

import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';
import {
	getConfluenceEditorClientId,
	InlineInputBoxSendMessageCommand,
	RovoStreamCompletedCommand,
	RovoStreamErroredCommand,
	RovoStreamAbortedCommand,
	ROVO_CHAT_CLIENT_ID,
} from '@atlassian/rovo-content-bridge-api-commands';

export type KeepPageStructureState = {
	/**
	 * Whether the keep page structure request is currently being processed
	 */
	isLoading: boolean;
	/**
	 * Error message if the request failed
	 */
	error: string | null;
	/**
	 * Whether the request was successful (edit was applied)
	 */
	isSuccess: boolean;
};

export type UseKeepPageStructureReturn = KeepPageStructureState & {
	/**
	 * Function to trigger the keep page structure edit
	 */
	keepPageStructure: () => void;
	/**
	 * Reset the state back to initial values
	 */
	reset: () => void;
};

export type UseKeepPageStructureOptions = {
	/**
	 * The content ID of the page (required for bridge communication)
	 */
	contentId?: string;
	/**
	 * Whether the feature is enabled. When false, the hook will not register
	 * with the bridge API.
	 */
	enabled?: boolean;
};

const KEEP_PAGE_STRUCTURE_PROMPT =
	`Update the document using the ConfluenceCreationTool. ` +
	`Convert it into a reusable template while strictly preserving page structure and macros ` +
	`but aggressively clearing user-specific content. ` +
	// ADF preamble
	`ADF CONTEXT: This document uses Atlassian Document Format (ADF). ` +
	`Macros are represented as extension nodes: bodiedExtension (macros with a body/content area), ` +
	`extension (macros with no body), and inlineExtension (inline macros). ` +
	`Preserve all extension nodes and their attrs entirely — these are the macro's identity and configuration. ` +
	`Only clear content inside a bodiedExtension's body when it is obviously user-generated content. ` +
	`For placeholder text color, use a textColor mark with color "#97a0af" (neutral gray). ` +
	`In ADF, newlines are represented as hardBreak nodes and blank lines are empty paragraph nodes — ` +
	`remove both when clearing content. ` +
	`Follow these rules exactly: ` +
	// 1. Page structure and headings
	`PAGE STRUCTURE AND HEADINGS: ` +
	`Do not change the page title text or any heading text (H1–H6). ` +
	`Do not add, remove, rename, or reorder any headings. ` +
	`Preserve the nesting and order of all structural blocks exactly as they are ` +
	`(headings, tables, panels, expands, layouts, code blocks, decision lists, action item lists, etc.). ` +
	`Non-structural blocks like user-content paragraphs, bullet lists, and ordered lists within a section will be replaced per the placeholder rules below. ` +
	`Do not flatten multi-column layouts or merge sections. Keep all layout sections and columns as they are. ` +
	// 2. Macros and special elements
	`MACROS AND SPECIAL ELEMENTS: ` +
	`Preserve all native Confluence macros in their current positions and configurations. ` +
	`Do not delete, add, reorder, or change the type of any macro. ` +
	`This includes: panels (info, note, warning, error, success), expand/collapse sections, ` +
	`decision lists, action items, status lozenges, dates, mentions, dividers, table of contents, and code blocks. ` +
	`Preserve all 3rd-party or unknown macros as structural containers: do NOT remove or change the macro itself. ` +
	`Do not alter macro parameters or configuration fields (such as settings, options, or structural flags), ` +
	`except where content must be cleared as described below. ` +
	// 3. What content to remove or replace
	`CONTENT TO REMOVE OR REPLACE: ` +
	`Treat the document's user-specific content as unsafe and aggressively clear it, while keeping the structural "shell" intact. ` +
	`Remove or clear all of the following wherever they appear in the content body: ` +
	`inline statuses (status lozenges), inline dates, inline mentions, inline smart links, ` +
	`and embedded content (iframes, embedded pages, embedded Jira issues, embed macros, and any embedCard or inlineCard nodes) ` +
	`(and any inline text that consists solely of those elements). ` +
	`Remove all link marks from any text that remains as a preserved element — ` +
	`if text is kept because it is part of a structural element (e.g., heading text, table header, panel title, label column), ` +
	`strip the link mark so the text is plain and unlinked. No hyperlinks should remain in the output. ` +
	`Text inside body paragraphs and list items that represent specific user-entered content. ` +
	`When clearing content from sections or table cells, remove all hardBreak nodes and empty paragraph nodes from the original content. ` +
	`Collapse all cleared content into a single clean block — do not leave behind blank lines, empty nodes, or whitespace artifacts. ` +
	`After clearing, replace content with structured placeholders as described in the placeholder rules below, ` +
	`so the overall structure and reading order of sections remains intact. ` +
	// 4. Placeholder strategy by heading section
	`PLACEHOLDER STRATEGY BY HEADING SECTION: ` +
	`Work at the level of each heading "section": a section is a heading and all normal body content ` +
	`(paragraphs and lists, plus any surrounding panels/expands/layouts) that belong to that heading, ` +
	`up until the next heading of the same or higher level. ` +
	`For each section that currently contains any user body text, inline statuses/dates/mentions/smart links, or images/diagrams: ` +
	`remove ALL original user content from that section: all paragraph nodes, list nodes, inline nodes (statuses, dates, mentions, smart links), ` +
	`hardBreak nodes, empty paragraph nodes, and any now-empty text nodes. ` +
	`After removal, the section should contain ONLY structural containers (panels, expands, layouts, tables) and one single placeholder. ` +
	`Insert exactly ONE concise placeholder paragraph for the entire section that describes what the user should write there. ` +
	`The placeholder should be bracketed: [Describe X...]. ` +
	`Be specific and instructive based on the heading text and nearby structure. ` +
	`Example under "Project Risks": [Summarize the main project risks, their likelihood, impact, and planned mitigations.] ` +
	`CRITICAL: Each heading section gets exactly ONE placeholder paragraph total — never more. ` +
	`Do NOT create additional paragraphs, list items, or duplicate placeholders inside panels, expands, or layout columns within the same section. ` +
	`Place the single placeholder at the top of the section content, before any structural containers. ` +
	`For sections that are already completely empty (no body text, no lists, no images, and no user content) before you start, ` +
	`leave them exactly as they are. Do not add placeholders into genuinely empty sections. ` +
	// 5. Placeholder styling
	`PLACEHOLDER STYLING: ` +
	`Apply italic formatting to all placeholder text so it is visually distinct from real content. ` +
	`If a neutral gray text color is supported without custom styling issues, apply a gray text color to placeholders as well. ` +
	`If you are not sure the color will be applied correctly, use italic only and skip the color. ` +
	`Do not modify the styling of surrounding structural elements (headings, panels, layouts, etc.) beyond inserting the placeholder text. ` +
	// 6. Inline statuses, dates, mentions, and smart links
	`INLINE STATUSES, DATES, MENTIONS, AND SMART LINKS: ` +
	`Explicitly remove all of the following from the user content: ` +
	`inline status lozenges, inline date macros, inline user or team mentions, ` +
	`inline smart links or link cards that refer to specific pages, issues, docs, or external URLs. ` +
	`Do not leave these elements or their original display text in the transformed template. ` +
	`If a paragraph or list item consists solely of these elements (with no other text), remove that paragraph or list item entirely. ` +
	`The structural placeholder at the section level is sufficient to guide users; ` +
	`you do NOT need to add one placeholder per removed inline element. ` +
	// 7. Images, screenshots, diagrams, and media
	`IMAGES, SCREENSHOTS, DIAGRAMS, AND MEDIA: ` +
	`Remove all images, screenshots, diagrams, and other visual media that represent user-specific content. ` +
	`Remove inline image/media nodes from the document body. ` +
	`Do not add explicit "[Insert image here]" placeholders at the exact location where the image was. ` +
	`Maintain the surrounding block structure (paragraphs, panels, layouts, headings, tables). ` +
	`If a section, panel body, or layout column contained only images or diagrams and no other text, ` +
	`clear the inner content but keep the surrounding container intact (panel, layout column, etc.). ` +
	`Do NOT add a separate placeholder inside the container — the section already has its single placeholder. ` +
	// 8. Tables
	`TABLES: ` +
	`Preserve all tables structurally: keep the same number of rows, columns, headers, and any table-level formatting. ` +
	`Remove user-specific content from table cells: clear the text content of all data cells. ` +
	`Remove inline statuses, dates, mentions, and smart links from within cells. ` +
	`Remove images or diagrams contained in table cells. ` +
	`Remove all text formatting artifacts from cleared cells, including: unordered list markers (bullets), ` +
	`ordered list numbers, checkboxes, bold/italic/strikethrough formatting, and any other inline formatting. ` +
	`Cleared cells should be completely empty — no residual markers, hardBreak nodes, empty paragraph nodes, or whitespace. ` +
	`Do NOT add per-cell placeholders; rely on the section-level placeholder for guidance. ` +
	`If the table contains 3rd-party or unknown macro content inside cells and you are not sure what is safe to remove, ` +
	`preserve the macro container but clear only obvious user-visible text, statuses, dates, mentions, smart links, and images from those cells. ` +
	// 9. Code blocks
	`CODE BLOCKS: ` +
	`Preserve every code block's position, language setting, and formatting. ` +
	`Remove the original code content. ` +
	`Replace it with a single comment-style placeholder that explains what kind of code belongs there. ` +
	`Example: // Add example usage of this API endpoint here. ` +
	`Do not add additional code lines beyond this single placeholder comment. ` +
	// 10. Decision lists and action items
	`DECISION LISTS AND ACTION ITEMS: ` +
	`Preserve all decision list and action item structures, including their positions and any non-content metadata. ` +
	`Explicitly empty the textual content of each decision or action item: ` +
	`remove any user-specific description text, and remove inline statuses, dates, mentions, and smart links contained in item descriptions. ` +
	`Replace each item's description with a brief, bracketed placeholder that explains the kind of content to provide. ` +
	`Example for a decision: [Describe the decision made, the rationale, and any trade-offs considered.] ` +
	`Example for an action item: [Describe the task, owner, and expected completion criteria.] ` +
	`Do not remove the items themselves or their list structure. ` +
	// 11. 3rd-party or unknown macros
	`3RD-PARTY OR UNKNOWN MACROS: ` +
	`Preserve all 3rd-party or unknown macros as structural containers. ` +
	`Where these macros clearly expose user-visible text, statuses, dates, mentions, smart links, or images ` +
	`in a way similar to normal content, you may clear that content following the same rules as above. ` +
	`If you are unsure whether text or elements inside a 3rd-party macro are configuration or content, ` +
	`err on the side of safety and leave them unchanged. ` +
	// 12. General safety rule
	`GENERAL SAFETY RULE: ` +
	`Make the minimum structural changes necessary to clear user-specific content and introduce helpful, section-level placeholders. ` +
	`Never change the overall layout, headings, or macro structure of the page. ` +
	`When anything is ambiguous or seems risky to change (especially inside unknown macros), ` +
	`prefer to leave the structural element intact and only remove obviously user-specific content. ` +
	// Control instructions
	`Do not open up the Rovo chat window. openChat=false.`;

const initialState: KeepPageStructureState = {
	isLoading: false,
	error: null,
	isSuccess: false,
};

export const useKeepPageStructure = ({
	contentId,
	enabled = true,
}: UseKeepPageStructureOptions = {}): UseKeepPageStructureReturn => {
	const [state, setState] = useState<KeepPageStructureState>(initialState);
	const isActiveRef = useRef(false);
	const contentIdRef = useRef<string | undefined>(contentId);

	// Keep contentIdRef in sync
	useEffect(() => {
		contentIdRef.current = contentId;
	}, [contentId]);

	const reset = useCallback(() => {
		setState(initialState);
		isActiveRef.current = false;
	}, []);

	// Use the same client ID as the editor so we receive the stream lifecycle events.
	// The conversation-assistant sends RovoStreamCompletedCommand targeted at
	// getConfluenceEditorClientId(contentId), so we must listen on the same ID.
	const editorClientId = getConfluenceEditorClientId(contentId || 'fallback');

	// Set up bridge API with handlers for streaming lifecycle events
	const { send, ready } = useRovoContentBridgeAPI(editorClientId, (on) => {
		// Handle stream completed - edit was successfully applied
		on(RovoStreamCompletedCommand, () => {
			if (isActiveRef.current) {
				setState({
					isLoading: false,
					error: null,
					isSuccess: true,
				});
				isActiveRef.current = false;
			}
		});

		// Handle stream error - edit failed
		on(RovoStreamErroredCommand, () => {
			if (isActiveRef.current) {
				setState({
					isLoading: false,
					error: 'Failed to apply edit',
					isSuccess: false,
				});
				isActiveRef.current = false;
			}
		});

		// Handle stream aborted - user cancelled
		on(RovoStreamAbortedCommand, () => {
			if (isActiveRef.current) {
				setState({
					isLoading: false,
					error: null,
					isSuccess: false,
				});
				isActiveRef.current = false;
			}
		});
	});

	// Mark the client as ready to receive messages just-in-time
	const hasCalledReadyRef = useRef(false);

	const keepPageStructure = useCallback(() => {
		if (enabled && !hasCalledReadyRef.current) {
			ready();
			hasCalledReadyRef.current = true;
		}

		// Validate required context
		if (!contentId) {
			setState({
				isLoading: false,
				error: 'Missing page context. Unable to keep page structure.',
				isSuccess: false,
			});
			return;
		}

		// Set loading state
		setState({
			isLoading: true,
			error: null,
			isSuccess: false,
		});

		// Track this request
		isActiveRef.current = true;

		try {
			send(InlineInputBoxSendMessageCommand, {
				receiverId: ROVO_CHAT_CLIENT_ID,
				prompt: KEEP_PAGE_STRUCTURE_PROMPT,
				source: 'CONFLUENCE_KEEP_PAGE_STRUCTURE',
				productKey: 'confluence',
				deepResearchEnabled: false,
				webSearchEnabled: false,
				contentId,
				openChat: false,
				addCurrentPageAsContextUserPreference: true,
			});
		} catch (error) {
			setState({
				isLoading: false,
				error: error instanceof Error ? error.message : 'Failed to send message to Rovo',
				isSuccess: false,
			});
			isActiveRef.current = false;
		}
	}, [send, contentId, enabled, ready]);

	return {
		...state,
		keepPageStructure,
		reset,
	};
};
